import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSWRConfig } from "swr";
import { DealClientTodoModalForm } from "~/components/common/deals/show/main/todos/DealClientTodoModalForm";
import { dealTodosCacheKey } from "~/hooks/deals/useDealTodos";
import { buildIndexPath } from "~/hooks/useCompanyDealTodos";
import type {
	CompanyDealTodo,
	CompanyDealTodoSearchCondition
} from "~/models/CompanyDealTodo";
import type { Deal } from "~/models/Deal";
import type { DealTodo, DealTodoEdit } from "~/models/DealTodo";
import { buildInitialDealTodoEdit, validationSchema } from "~/models/DealTodo";
import type { Staff } from "~/models/Staff";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/deals/DealTodo";
import { onInvalidScroll } from "~/utils/FormUtil";

type Props = DeepReadonly<{
	deal: Deal | CompanyDealTodo["deal"];
	handleClose: () => void;
	staffs: Staff[];
	todo?: DealTodo | CompanyDealTodo | null;
}> & {
	searchCondition?: CompanyDealTodoSearchCondition;
};

export function DealClientTodoModal({
	deal,
	staffs,
	handleClose,
	todo = null,
	searchCondition = {}
}: Props) {
	const { notify } = useNotification();
	const { mutate: globalMutate } = useSWRConfig();
	const methods = useForm<DealTodoEdit>({
		defaultValues: todo ?? buildInitialDealTodoEdit(),
		mode: "onChange",
		resolver: yupResolver(validationSchema),
		shouldFocusError: false
	});
	useEffect(() => {
		if (methods.watch("staff") === null) {
			methods.setValue("staff", deal.staff);
		}
	}, [todo, methods, deal]);
	const mutateTodo = useCallback(async (): Promise<void> => {
		await globalMutate(dealTodosCacheKey(deal.id));
		await globalMutate(buildIndexPath(searchCondition));
	}, [deal.id, globalMutate, searchCondition]);
	const handleModalSubmit = useCallback(() => {
		methods.handleSubmit(
			async (submitValue: DealTodoEdit) => {
				if (todo === null) {
					const response = await create(deal.id, submitValue);
					if (isSuccess(response)) {
						await mutateTodo();
						handleClose();
						notify({ content: "TODOを追加しました。", variant: "success" });
						return;
					}
					notify({ content: "エラーが発生しました。", variant: "error" });
					return;
				}
				const response = await update(deal.id, todo.id, submitValue);
				if (isSuccess(response)) {
					await mutateTodo();
					handleClose();
					notify({ content: "TODOを更新しました。", variant: "success" });
					return;
				}
				notify({ content: "エラーが発生しました。", variant: "error" });
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [deal.id, handleClose, methods, mutateTodo, notify, todo]);
	if (deal === null) return null;
	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title={todo ? "TODOを編集" : "TODOを追加"} />
			<ModalBody>
				<FormProvider {...methods}>
					<DealClientTodoModalForm deal={deal} staffs={staffs} />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={methods.formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						保存
					</Button>
				}
			/>
		</Modalv2>
	);
}
